import React, { Component } from 'react';
import { SaveButton, DeleteButton } from '../modalButtons';

const MOODS = ['concise', 'friendly', 'excited', 'professional'];

// Models the diva-ai engine actually runs (see diva-ai/engine.js `defaults.models`
// + `dispatchModels`). The engine infers the provider from the model-name prefix
// (claude* → Anthropic, gpt*/o# → OpenAI), so `id` must stay in sync with what the
// engine supports. `description` is the tier shown next to the name in the dropdown.
// Gemini is intentionally absent — the Gemini provider isn't built yet.
const LLM_MODELS = [
	{ id: 'gpt-5.4', label: 'gpt-5.4', description: 'Balanced · current-gen value' },
	{ id: 'gpt-5.4-mini', label: 'gpt-5.4-mini', description: 'Budget · fast and capable' },
	{ id: 'gpt-5.4-nano', label: 'gpt-5.4-nano', description: 'Cheapest · lightweight tasks' },
	{ id: 'gpt-4o', label: 'gpt-4o', description: 'Balanced · previous-gen' },
	{ id: 'gpt-4o-mini', label: 'gpt-4o-mini', description: 'Budget · previous-gen' },
	{ id: 'claude-opus-4-8', label: 'claude-opus-4-8', description: 'Premium · most capable' },
	{ id: 'claude-sonnet-4-6', label: 'claude-sonnet-4-6', description: 'Balanced · speed and quality' },
	{ id: 'claude-haiku-4-5', label: 'claude-haiku-4-5', description: 'Budget · fastest' }
];
// Capabilities are the per-bot options shown in the editor. Call-control that every
// bot always has — hang-up, and the legacy "routeTo" hand-off (now automatic via the
// engine's sticky dispatcher) — is intentionally NOT listed here; the engine grants
// it to all bots, so surfacing a toggle would be misleading.
const BOT_CAPABILITIES = [
	{ id: 'knowledgeBase', label: 'Knowledge Base' },
	{ id: 'integrations', label: 'Integrations' },
	{ id: 'transferAgent', label: 'Transfer' },
];

// Only TTS providers Twilio ConversationRelay accepts natively. `id` is the value
// stored on the chatbot AND passed straight through to ConversationRelay's
// `ttsProvider` attribute by diva-ai/twilio.js — anything else gets rejected with
// TwiML error 64101. `canPreview` reflects what the server-side previewVoice route
// can synthesize (Amazon via polly-tts; Google/ElevenLabs have no preview path yet).
const VOICE_PROVIDERS = [
	{ id: 'Google', label: 'Google', description: 'Natural, lifelike voices. A great all-around choice.', canPreview: false },
	{ id: 'Amazon', label: 'Amazon Polly', description: 'Clear, reliable voices with a wide range to pick from.', canPreview: true },
	{ id: 'ElevenLabs', label: 'ElevenLabs', description: 'The most expressive, human-like voices for premium calls.', canPreview: false }
];

// `id` is the literal `voice` value ConversationRelay expects for that provider:
//   Google     → Google Cloud TTS voice names (e.g. en-US-Journey-O)
//   Amazon     → Polly voice + engine suffix (e.g. Joanna-Neural)
//   ElevenLabs → ElevenLabs voice IDs
const VOICES_BY_PROVIDER = {
	// Google voice ids are opaque codes, so we give them friendly display names.
	Google: [
		{ id: 'en-US-Journey-O', label: 'Aria', desc: 'Female · warm and conversational' },
		{ id: 'en-US-Journey-F', label: 'Skylar', desc: 'Female · bright and expressive' },
		{ id: 'en-US-Journey-D', label: 'Marcus', desc: 'Male · warm and easygoing' },
		{ id: 'en-US-Neural2-C', label: 'Nora', desc: 'Female · clear and professional' },
		{ id: 'en-US-Neural2-A', label: 'Evan', desc: 'Male · steady and confident' },
		{ id: 'en-US-Neural2-J', label: 'Caleb', desc: 'Male · friendly and approachable' }
	],
	Amazon: [
		{ id: 'Joanna-Neural', label: 'Joanna', desc: 'Female · smooth and professional' },
		{ id: 'Kendra-Neural', label: 'Kendra', desc: 'Female · calm and articulate' },
		{ id: 'Kimberly-Neural', label: 'Kimberly', desc: 'Female · upbeat and friendly' },
		{ id: 'Salli-Neural', label: 'Salli', desc: 'Female · bright and energetic' },
		{ id: 'Ruth-Neural', label: 'Ruth', desc: 'Female · warm and natural' },
		{ id: 'Joey-Neural', label: 'Joey', desc: 'Male · relaxed and conversational' },
		{ id: 'Justin-Neural', label: 'Justin', desc: 'Male · youthful and clear' },
		{ id: 'Kevin-Neural', label: 'Kevin', desc: 'Male · casual and approachable' },
		{ id: 'Matthew-Neural', label: 'Matthew', desc: 'Male · deep and authoritative' },
		{ id: 'Stephen-Neural', label: 'Stephen', desc: 'Male · polished and confident' }
	],
	ElevenLabs: [
		{ id: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel', desc: 'Female · calm and soothing' },
		{ id: 'EXAVITQu4vr4xnSDxMaL', label: 'Bella', desc: 'Female · soft and gentle' },
		{ id: 'MF3mGyEYCl7XYWbV9V6O', label: 'Elli', desc: 'Female · young and lively' },
		{ id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam', desc: 'Male · deep and resonant' },
		{ id: 'ErXwobaYiN019PkySvjV', label: 'Antoni', desc: 'Male · warm and well-rounded' },
		{ id: 'TxGEqnHWrfWFTfGW9XjX', label: 'Josh', desc: 'Male · young and energetic' }
	]
};

const ACTION_COLORS = {
	knowledgeBase: '#27ae60',
	integrations: '#8e44ad',
	transferAgent: '#e67e22',
	lookupRoute: '#27ae60',
	lookupTicket: '#8e44ad'
};

const MOOD_COLORS = {
	concise: '#7f8c8d',
	friendly: '#27ae60',
	excited: '#e67e22',
	professional: '#2A6AA9'
};

// Failure-handling: the reasons a call can be counted as failing (the user picks which
// count) and the actions taken once the threshold is crossed. Mirrors the diva-ai
// interface (twilio.js/cli.js failurePolicy). Stored as chatbot.failurePolicy.
const FAILURE_TRIGGERS = [
	{ id: 'silence', label: 'Silence / No input', desc: 'The caller said nothing, or the system could not make out any speech on a turn.' },
	{ id: 'unresolved', label: 'Data not available', desc: "The assistant had no answer — it deflected, said it didn't have the information, or only offered a person because it couldn't help." },
	{ id: 'toolError', label: 'System error', desc: 'An action or integration returned an error, or something went wrong while handling the turn.' },
	{ id: 'frustrated', label: 'Frustration detected', desc: 'The caller sounds upset, keeps repeating an unmet request, or says things are not working.' }
];
const FAILURE_ACTIONS = [
	{ id: 'hangup', label: 'Hang up with message' },
	{ id: 'transfer', label: 'Queue' },
	{ id: 'flow', label: 'Send to IVR flow' }
];
// When the chosen queue is outside its business hours, what to do instead.
const QUEUE_CLOSED_ACTIONS = [
	{ id: 'disconnect', label: 'Disconnect' },
	{ id: 'flow', label: 'Send to IVR flow' }
];

const cardStyle = {
	background: '#fff',
	border: '1px solid #e0e6ef',
	borderRadius: '8px',
	boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
	padding: '20px',
	display: 'flex',
	flexDirection: 'column'
};

const cardHeaderStyle = {
	fontSize: '11px',
	fontWeight: '700',
	letterSpacing: '1px',
	textTransform: 'uppercase',
	color: '#888',
	marginBottom: '10px'
};

const modalLabelStyle = {
	display: 'block',
	fontSize: '12px',
	color: '#888',
	marginBottom: '6px',
	fontWeight: '700',
	textTransform: 'uppercase',
	letterSpacing: '0.5px'
};

const modalSelectStyle = {
	width: '100%',
	padding: '9px 10px',
	border: '1px solid #ccc',
	borderRadius: '6px',
	fontSize: '14px',
	background: '#fff',
	color: '#333'
};

class AIChatbots extends Component {
	constructor(props) {
		super(props);
		this.state = {
			editingIntro: false,
			editingAI: false,
			editingMood: false,
			editingFailure: false,
			editingBot: false,
			deletingBot: false,
			addingChatbot: false,
			deletingChatbot: false,
			renamingChatbot: false,
			chatbotMenuOpen: false,
			newChatbotName: '',
			activeChatbot: '',
			tempIntro: '',
			tempGoodbye: '',
			tempAI: {},
			tempMood: '',
			tempFailure: {},
			tempBot: {},
			playingVoice: null,
			audioGenerating: false,
			generatingIVR: false,
			loading: false,
			error: '',
			draft: {},
			isDirty: false
		};
		this._previewTimeout = null;
	}

	cancel = () => {
		if (this._previewTimeout) { clearTimeout(this._previewTimeout); this._previewTimeout = null; }
		this.setState({
			editingIntro: false, editingAI: false, editingMood: false, editingFailure: false,
			editingBot: false, deletingBot: false,
			addingChatbot: false, deletingChatbot: false, renamingChatbot: false, chatbotMenuOpen: false,
			loading: false, error: '', playingVoice: null, audioGenerating: false, generatingIVR: false
		});
	}

	renderPreviewBtn = (voiceId, onClick) => {
		var isThis = this.state.playingVoice === voiceId;
		var busy = !!this.state.playingVoice;
		var generating = isThis && this.state.audioGenerating;
		var playing = isThis && !this.state.audioGenerating;
		var btnStyle = {
			background: isThis ? '#e8f0fa' : '#f5f7fa',
			border: '1px solid ' + (isThis ? '#2A6AA9' : '#ddd'),
			borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '600',
			cursor: busy ? 'default' : 'pointer',
			color: isThis ? '#2A6AA9' : '#666',
			flexShrink: 0, marginLeft: '10px', minWidth: '90px', textAlign: 'center',
			display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
			opacity: (busy && !isThis) ? 0.4 : 1
		};
		return (
			<button onClick={onClick} disabled={busy} style={btnStyle}>
				{generating && (
					<span style={{ width: '11px', height: '11px', border: '2px solid #c0d4ee', borderTopColor: '#2A6AA9', borderRadius: '50%', display: 'inline-block', animation: 'acSpin 0.7s linear infinite', flexShrink: 0 }} />
				)}
				{playing && (
					<span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2px', height: '12px' }}>
						{[0, 0.15, 0.3].map(function (delay, i) {
							return <span key={i} style={{ width: '3px', background: '#2A6AA9', borderRadius: '2px', height: '100%', transformOrigin: 'bottom', animation: 'acBar 0.7s ease infinite', animationDelay: delay + 's' }} />;
						})}
					</span>
				)}
				{generating ? 'Generating' : playing ? 'Playing' : '▶ Preview'}
			</button>
		);
	}

	componentWillReceiveProps(nextProps) {
		// Handle IVR generation completion
		if (this.state.generatingIVR) {
			var prevResult = (this.props.data._ivrGenResult || {}).ts;
			var nextResult = (nextProps.data._ivrGenResult || {}).ts;
			if (nextResult && nextResult !== prevResult) {
				this.setState({ generatingIVR: false });
				window.location.hash = 'IVR Flows';
				return;
			}
		}
		if (this.state.loading) {
			var incoming = Object.assign({}, nextProps.data.AIChatbots || {});
			delete incoming._trialReply;
			var incomingChatbots = incoming.chatbots || {};
			var firstKey = Object.keys(incomingChatbots)[0] || '';
			this.setState({ loading: false, isDirty: false, draft: JSON.parse(JSON.stringify(incoming)), activeChatbot: this.state.activeChatbot || firstKey });
			return;
		}
		if (!this.state.isDirty) {
			this.setState({ draft: JSON.parse(JSON.stringify(nextProps.data.AIChatbots || {})) });
		}
	}

	generateIVR = () => {
		if (!this.state.activeChatbot) return;
		this.setState({ generatingIVR: true });
		this.props.ws.send(JSON.stringify({ route: 'generateIVRFromChatbot', chatbotKey: this.state.activeChatbot }));
	}

	componentDidMount() {
		var style = document.createElement('style');
		style.id = 'aichatbots-anim';
		style.textContent = '@keyframes acSpin{to{transform:rotate(360deg)}}@keyframes acBar{0%,100%{transform:scaleY(0.3)}50%{transform:scaleY(1)}}';
		document.head.appendChild(style);
		var chatbotData = this.props.data.AIChatbots || {};
		var chatbots = chatbotData.chatbots || {};
		var firstKey = Object.keys(chatbots)[0] || '';
		this.setState({ draft: JSON.parse(JSON.stringify(chatbotData)), activeChatbot: firstKey });
	}
	componentWillUnmount() {
		var style = document.getElementById('aichatbots-anim');
		if (style) style.remove();
		if (this._previewTimeout) clearTimeout(this._previewTimeout);
	}

	// ── Chatbot management ─────────────────────────────────────────────────
	getActiveChatbot = () => {
		var draft = this.state.draft || {};
		return (draft.chatbots || {})[this.state.activeChatbot] || {};
	}
	setActiveChatbotData = (updater) => {
		var draft = JSON.parse(JSON.stringify(this.state.draft || {}));
		if (!draft.chatbots) draft.chatbots = {};
		if (!draft.chatbots[this.state.activeChatbot]) draft.chatbots[this.state.activeChatbot] = {};
		updater(draft.chatbots[this.state.activeChatbot]);
		this.setState({ draft: draft, isDirty: true });
	}
	openAddChatbot = () => {
		this.setState({ addingChatbot: true, newChatbotName: '', error: '' });
	}
	confirmAddChatbot = () => {
		var name = this.state.newChatbotName.trim();
		if (!name) { this.setState({ error: 'Please enter a name.' }); return; }
		var key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
		if (!key) { this.setState({ error: 'Name must contain letters or numbers.' }); return; }
		var draft = JSON.parse(JSON.stringify(this.state.draft || {}));
		if (!draft.chatbots) draft.chatbots = {};
		if (draft.chatbots[key]) { this.setState({ error: 'A virtual agent with that name already exists.' }); return; }
		draft.chatbots[key] = {
			name: name,
			introMessage: '',
			goodbyeMessage: '',
			mood: { current: 'friendly', moods: { concise: { description: 'Be brief and direct. Give the essential information in 1-2 sentences.', lengthGuidance: '1-2 sentences' }, friendly: { description: 'Be warm, conversational, and genuinely helpful.', lengthGuidance: '2-3 sentences' }, excited: { description: 'Be enthusiastic and upbeat!', lengthGuidance: '2-3 sentences' }, professional: { description: 'Be polished, courteous, and thorough.', lengthGuidance: '2-3 sentences' } } },
			ai: { provider: 'openai', ttsProvider: 'Google', ttsVoice: 'en-US-Journey-O' },
			bots: {}
		};
		this.setState({ draft: draft, isDirty: true, addingChatbot: false, activeChatbot: key, newChatbotName: '', error: '' });
	}
	openDeleteChatbot = () => {
		this.setState({ deletingChatbot: true, chatbotMenuOpen: false });
	}
	openRenameChatbot = () => {
		var draft = this.state.draft || {};
		var current = (draft.chatbots || {})[this.state.activeChatbot] || {};
		this.setState({ renamingChatbot: true, chatbotMenuOpen: false, newChatbotName: current.name || '', error: '' });
	}
	confirmRenameChatbot = () => {
		var name = this.state.newChatbotName.trim();
		if (!name) { this.setState({ error: 'Please enter a name.' }); return; }
		var draft = JSON.parse(JSON.stringify(this.state.draft || {}));
		draft.chatbots[this.state.activeChatbot].name = name;
		this.setState({ draft: draft, isDirty: true, renamingChatbot: false, newChatbotName: '', error: '' });
	}
	confirmDeleteChatbot = () => {
		var draft = JSON.parse(JSON.stringify(this.state.draft || {}));
		delete draft.chatbots[this.state.activeChatbot];
		var remaining = Object.keys(draft.chatbots || {});
		this.setState({ draft: draft, isDirty: true, deletingChatbot: false, activeChatbot: remaining[0] || '' });
	}
	duplicateChatbot = () => {
		var draft = JSON.parse(JSON.stringify(this.state.draft || {}));
		var source = draft.chatbots[this.state.activeChatbot];
		var newName = (source.name || this.state.activeChatbot) + ' Copy';
		var newKey = newName.toLowerCase().replace(/[^a-z0-9]/g, '');
		var i = 2;
		while (draft.chatbots[newKey]) { newKey = newName.toLowerCase().replace(/[^a-z0-9]/g, '') + i; i++; }
		draft.chatbots[newKey] = JSON.parse(JSON.stringify(source));
		draft.chatbots[newKey].name = newName;
		this.setState({ draft: draft, isDirty: true, chatbotMenuOpen: false, activeChatbot: newKey });
	}

	// ── Intro ──────────────────────────────────────────────────────────────
	openEditIntro = () => {
		var cb = this.getActiveChatbot();
		this.setState({ editingIntro: true, tempIntro: cb.introMessage || '', tempGoodbye: cb.goodbyeMessage || '', error: '' });
	}
	saveIntro = () => {
		this.setActiveChatbotData(function (cb) {
			cb.introMessage = this.state.tempIntro;
			cb.goodbyeMessage = this.state.tempGoodbye;
		}.bind(this));
		this.setState({ editingIntro: false, error: '' });
	}
	previewText = (id, text) => {
		if (this.state.playingVoice || !text || !text.trim()) return;
		var ai = this.getActiveChatbot().ai || {};
		this.setState({ playingVoice: id, audioGenerating: true });
		this.props.ws.send(JSON.stringify({
			route: 'previewVoice',
			provider: ai.ttsProvider,
			voice: ai.ttsVoice,
			text: text
		}));
		this._previewTimeout = setTimeout(() => this.setState({ playingVoice: null, audioGenerating: false }), 30000);
	}

	// ── AI & Voice ─────────────────────────────────────────────────────────
	openEditAI = () => {
		var cb = this.getActiveChatbot();
		this.setState({
			editingAI: true,
			tempAI: { ai: JSON.parse(JSON.stringify(cb.ai || {})) },
			error: ''
		});
	}
	editAIField = (section, field) => (e) => {
		var tempAI = JSON.parse(JSON.stringify(this.state.tempAI));
		if (!tempAI[section]) tempAI[section] = {};
		tempAI[section][field] = e.target.value;
		this.setState({ tempAI: tempAI });
	}
	selectProvider = (providerId) => {
		var tempAI = JSON.parse(JSON.stringify(this.state.tempAI));
		if (!tempAI.ai) tempAI.ai = {};
		tempAI.ai.ttsProvider = providerId;
		var voices = VOICES_BY_PROVIDER[providerId] || [];
		if (voices.length > 0) {
			var currentVoice = tempAI.ai.ttsVoice;
			if (!currentVoice || !voices.find(function (v) { return v.id === currentVoice; })) {
				tempAI.ai.ttsVoice = voices[0].id;
			}
		}
		this.setState({ tempAI: tempAI });
	}
	selectVoice = (voiceId) => {
		var tempAI = JSON.parse(JSON.stringify(this.state.tempAI));
		if (!tempAI.ai) tempAI.ai = {};
		tempAI.ai.ttsVoice = voiceId;
		this.setState({ tempAI: tempAI });
	}
	onAudioStart = () => {
		this.setState({ audioGenerating: false });
	}
	onAudioEnd = () => {
		if (this._previewTimeout) { clearTimeout(this._previewTimeout); this._previewTimeout = null; }
		this.setState({ playingVoice: null, audioGenerating: false });
	}
	previewCurrentVoice = () => {
		if (this.state.playingVoice) return;
		var ai = this.getActiveChatbot().ai || {};
		this.setState({ playingVoice: '_card', audioGenerating: true });
		this.props.ws.send(JSON.stringify({
			route: 'previewVoice',
			provider: ai.ttsProvider,
			voice: ai.ttsVoice
		}));
		this._previewTimeout = setTimeout(() => this.setState({ playingVoice: null, audioGenerating: false }), 30000);
	}
	previewVoice = (voiceId) => {
		if (this.state.playingVoice) return;
		var ai = this.state.tempAI.ai || {};
		this.setState({ playingVoice: voiceId, audioGenerating: true });
		this.props.ws.send(JSON.stringify({
			route: 'previewVoice',
			provider: ai.ttsProvider,
			voice: voiceId
		}));
		this._previewTimeout = setTimeout(() => this.setState({ playingVoice: null, audioGenerating: false }), 30000);
	}
	saveAI = () => {
		this.setActiveChatbotData(function (cb) { cb.ai = this.state.tempAI.ai; }.bind(this));
		this.setState({ editingAI: false, error: '' });
	}

	// ── Mood ───────────────────────────────────────────────────────────────
	openEditMood = () => {
		var cb = this.getActiveChatbot();
		this.setState({ editingMood: true, tempMood: (cb.mood && cb.mood.current) || 'friendly', error: '' });
	}
	saveMood = () => {
		this.setActiveChatbotData(function (cb) {
			if (!cb.mood) cb.mood = {};
			cb.mood.current = this.state.tempMood;
		}.bind(this));
		this.setState({ editingMood: false, error: '' });
	}

	// ── Failure handling ────────────────────────────────────────────────────
	openEditFailure = () => {
		var cb = this.getActiveChatbot();
		var fp = cb.failurePolicy || {};
		var t = fp.triggers || {};
		this.setState({
			editingFailure: true,
			tempFailure: {
				maxFailedAttempts: fp.maxFailedAttempts || 3,
				// default every reason ON when unset (=== false means explicitly disabled)
				triggers: {
					silence: t.silence !== false,
					unresolved: t.unresolved !== false,
					toolError: t.toolError !== false,
					frustrated: t.frustrated !== false
				},
				action: fp.action || 'hangup',
				message: fp.message || '',
				fallbackQueueId: fp.fallbackQueueId || '',
				fallbackFlowId: fp.fallbackFlowId || '',
				closedAction: fp.closedAction || 'disconnect',
				closedFlowId: fp.closedFlowId || ''
			},
			error: ''
		});
	}
	setFailureField = (field) => (e) => {
		var val = field === 'maxFailedAttempts' ? (parseInt(e.target.value) || 0) : e.target.value;
		this.setState({ tempFailure: Object.assign({}, this.state.tempFailure, { [field]: val }) });
	}
	toggleFailureTrigger = (key) => () => {
		var triggers = Object.assign({}, this.state.tempFailure.triggers);
		triggers[key] = !triggers[key];
		this.setState({ tempFailure: Object.assign({}, this.state.tempFailure, { triggers: triggers }) });
	}
	saveFailure = () => {
		this.setActiveChatbotData(function (cb) { cb.failurePolicy = this.state.tempFailure; }.bind(this));
		this.setState({ editingFailure: false, error: '' });
	}

	// ── Bots ───────────────────────────────────────────────────────────────
	openEditBot = (botId, bot) => (e) => {
		this.setState({
			editingBot: { ...JSON.parse(JSON.stringify(bot)), _botId: botId, _originalName: bot.name },
			error: ''
		});
	}
	openAddBot = () => {
		this.setState({
			editingBot: {
				_botId: '',
				_isNew: true,
				name: '',
				description: '',
				model: 'gpt-4o-mini',
				maxTokens: 500,
				maxHistory: 8,
				systemPrompt: '',
				routingHints: '',
				actions: ['transferAgent'],
				knowledgeIds: []
			},
			error: ''
		});
	}
	openDeleteBot = (botId, bot) => (e) => {
		e.stopPropagation();
		this.setState({ deletingBot: { botId, bot } });
	}
	editBotField = (field) => (e) => {
		var tempBot = Object.assign({}, this.state.editingBot);
		tempBot[field] = field === 'maxTokens' || field === 'maxHistory'
			? parseInt(e.target.value) || 0
			: e.target.value;
		this.setState({ editingBot: tempBot });
	}
	toggleBotAction = (action) => () => {
		var tempBot = JSON.parse(JSON.stringify(this.state.editingBot));
		var idx = tempBot.actions.indexOf(action);
		if (idx > -1) {
			tempBot.actions.splice(idx, 1);
		} else {
			tempBot.actions.push(action);
		}
		this.setState({ editingBot: tempBot });
	}
	saveBot = () => {
		var bot = Object.assign({}, this.state.editingBot);
		var botId = bot._botId;
		var isNew = bot._isNew;
		if (!bot.name || bot.name.trim().length < 1) {
			this.setState({ error: 'Please enter a name for this bot.' });
			return;
		}
		if (isNew && !botId) {
			botId = bot.name.toLowerCase().replace(/[^a-z0-9]/g, '');
		}
		delete bot._botId;
		delete bot._isNew;
		this.setActiveChatbotData(function (cb) {
			if (!cb.bots) cb.bots = {};
			cb.bots[botId] = bot;
		});
		this.setState({ editingBot: false, error: '' });
	}
	confirmDeleteBot = () => {
		this.setActiveChatbotData(function (cb) {
			delete cb.bots[this.state.deletingBot.botId];
		}.bind(this));
		this.setState({ deletingBot: false });
	}

	toggleBotKnowledge = (kId) => () => {
		var tempBot = JSON.parse(JSON.stringify(this.state.editingBot));
		if (!tempBot.knowledgeIds) tempBot.knowledgeIds = [];
		var idx = tempBot.knowledgeIds.indexOf(kId);
		if (idx > -1) { tempBot.knowledgeIds.splice(idx, 1); } else { tempBot.knowledgeIds.push(kId); }
		this.setState({ editingBot: tempBot });
	}

	toggleBotActionId = (aId) => () => {
		var tempBot = JSON.parse(JSON.stringify(this.state.editingBot));
		if (!tempBot.actionIds) tempBot.actionIds = [];
		var idx = tempBot.actionIds.indexOf(aId);
		if (idx > -1) { tempBot.actionIds.splice(idx, 1); } else { tempBot.actionIds.push(aId); }
		this.setState({ editingBot: tempBot });
	}

	publish = () => {
		this.setState({ loading: true });
		var toPublish = JSON.parse(JSON.stringify(this.state.draft || {}));
		delete toPublish._trialReply;
		this.props.ws.send(JSON.stringify({ route: 'publishAIChatbots', chatbots: toPublish }));
	}

	checkEnter = (e) => {
		if (e.keyCode === 13 && !e.shiftKey) {
			if (this.state.editingIntro) this.saveIntro();
		}
	}

	render() {
		if (!this.props.active) return <div width="100%"></div>;

		var allDraft = this.state.draft || {};
		var chatbots = allDraft.chatbots || {};
		var chatbotKeys = Object.keys(chatbots);
		var activeKey = this.state.activeChatbot || chatbotKeys[0] || '';
		var data = chatbots[activeKey] || {};
		var ai = data.ai || {};
		var mood = data.mood || {};
		var bots = data.bots || {};
		var knowledge = this.props.data.KnowledgeBase || {};
		var queuesData = ((this.props.data.Queues || {}).data) || {};   // { [id]: { name, ... } }
		var flowsData = ((this.props.data.IVRFlows || {}).data) || {};  // { [id]: { id, name, ... } }
		var currentMood = mood.current || 'friendly';
		var currentMoodDef = (mood.moods && mood.moods[currentMood]) || {};

		var routerBot = bots.router || null;
		var otherBots = Object.entries(bots).filter(function (e) { return e[0] !== 'router'; });

		var clickableCardStyle = { ...cardStyle, cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s' };

		// ── Top Cards ──────────────────────────────────────────────────────
		var introCard = (
			<div style={{ ...clickableCardStyle, flex: '1 1 280px', minWidth: '220px' }} onClick={this.openEditIntro}>
				<div style={cardHeaderStyle}>Messages</div>
				<div style={{ marginBottom: '12px' }}>
					<div style={{ fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Intro</div>
					<div style={{ fontSize: '14px', lineHeight: '1.5', fontStyle: data.introMessage ? 'normal' : 'italic', color: data.introMessage ? '#222' : '#aaa' }}>
						{data.introMessage || 'No intro message set'}
					</div>
				</div>
				<div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
					<div style={{ fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Goodbye</div>
					<div style={{ fontSize: '14px', lineHeight: '1.5', fontStyle: data.goodbyeMessage ? 'normal' : 'italic', color: data.goodbyeMessage ? '#222' : '#aaa' }}>
						{data.goodbyeMessage || 'No goodbye message set'}
					</div>
				</div>
			</div>
		);

		var aiProviderLabel = (VOICE_PROVIDERS.find(function (p) { return p.id === ai.ttsProvider; }) || {}).label || (ai.ttsProvider || '—');
		var aiVoiceDef = ai.ttsProvider && ai.ttsVoice
			? ((VOICES_BY_PROVIDER[ai.ttsProvider] || []).find(function (v) { return v.id === ai.ttsVoice; }) || {})
			: {};
		var aiProviderCanPreview = (VOICE_PROVIDERS.find(function (p) { return p.id === ai.ttsProvider; }) || {}).canPreview;
		var aiCard = (
			<div style={{ ...clickableCardStyle, flex: '1 1 280px', minWidth: '220px' }} onClick={this.openEditAI}>
				<div style={cardHeaderStyle}>AI & Voice Settings</div>
				<div style={{ fontSize: '24px', fontWeight: '700', color: '#002f5f', textTransform: 'capitalize', marginBottom: '4px' }}>
					{aiVoiceDef.label || ai.ttsVoice || '—'}
				</div>
				{aiVoiceDef.desc && (
					<div style={{ fontSize: '13px', color: '#888', fontStyle: 'italic', marginBottom: '14px' }}>{aiVoiceDef.desc}</div>
				)}
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
					<span style={{ background: '#e8f0fa', color: '#2A6AA9', borderRadius: '4px', padding: '3px 9px', fontSize: '12px', fontWeight: '600' }}>{aiProviderLabel}</span>
				</div>
				{aiProviderCanPreview && ai.ttsVoice && (
					<div style={{ alignSelf: 'flex-start' }} onClick={(e) => e.stopPropagation()}>
						{this.renderPreviewBtn('_card', this.previewCurrentVoice)}
					</div>
				)}
			</div>
		);

		var moodCard = (
			<div style={{ ...clickableCardStyle, flex: '1 1 240px', minWidth: '200px' }} onClick={this.openEditMood}>
				<div style={cardHeaderStyle}>Tone & Mood</div>
				<div style={{ marginBottom: '8px' }}>
					<span style={{
						display: 'inline-block',
						backgroundColor: MOOD_COLORS[currentMood] || '#888',
						color: '#fff',
						borderRadius: '20px',
						padding: '4px 14px',
						fontWeight: '600',
						fontSize: '14px',
						textTransform: 'capitalize'
					}}>{currentMood}</span>
				</div>
				<div style={{ fontSize: '13px', color: '#666', lineHeight: '1.5', flex: 1 }}>
					{currentMoodDef.description || ''}
				</div>
				<div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
					{currentMoodDef.lengthGuidance ? 'Length: ' + currentMoodDef.lengthGuidance : ''}
				</div>
			</div>
		);

		var fp = data.failurePolicy || {};
		var fpActionLabel = (FAILURE_ACTIONS.find(function (a) { return a.id === (fp.action || 'hangup'); }) || {}).label || 'Hang up';
		var fpDest = fp.action === 'transfer'
			? ((queuesData[fp.fallbackQueueId] || {}).name || '—')
			: (fp.action === 'flow' ? ((flowsData[fp.fallbackFlowId] || {}).name || '—') : '');
		var fpTriggers = fp.triggers || {};
		var fpEnabled = FAILURE_TRIGGERS.filter(function (t) { return fpTriggers[t.id] !== false; });
		var failureCard = (
			<div style={{ ...clickableCardStyle, flex: '1 1 240px', minWidth: '200px' }} onClick={this.openEditFailure}>
				<div style={cardHeaderStyle}>Failure Handling</div>
				{data.failurePolicy ? (
					<div style={{ flex: 1 }}>
						<div style={{ fontSize: '14px', color: '#222', lineHeight: '1.5', marginBottom: '8px' }}>
							After <strong>{fp.maxFailedAttempts || 3}</strong> failed attempts → <strong>{fpActionLabel}</strong>{fpDest ? ' (' + fpDest + ')' : ''}
						</div>
						<div style={{ fontSize: '12px', color: '#888' }}>Counts: {fpEnabled.map(function (t) { return t.label; }).join(', ') || 'nothing'}</div>
					</div>
				) : (
					<div style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic', flex: 1 }}>Default: 3 attempts → hang up. Click to configure.</div>
				)}
			</div>
		);

		// ── Bot Cards ──────────────────────────────────────────────────────
		var renderActionChips = function (actions) {
			// Only show chips for real per-bot capabilities. Legacy/universal entries
			// that may linger in stored config (e.g. 'hangup', 'routeTo') are skipped —
			// the engine grants those to every bot, so they aren't bot-specific options.
			return (actions || []).filter(function (a) {
				return BOT_CAPABILITIES.some(function (c) { return c.id === a; });
			}).map(function (a) {
				var cap = BOT_CAPABILITIES.find(function (c) { return c.id === a; });
				var label = cap ? cap.label : a;
				return (
					<span key={a} style={{
						display: 'inline-block',
						backgroundColor: ACTION_COLORS[a] || '#666',
						color: '#fff',
						borderRadius: '4px',
						padding: '2px 7px',
						fontSize: '11px',
						fontWeight: '600',
						marginRight: '4px',
						marginBottom: '4px'
					}}>{label}</span>
				);
			});
		};

		var routerCard = routerBot ? (
			<div onClick={this.openEditBot('router', routerBot)} style={{
				...clickableCardStyle,
				borderLeft: '4px solid #2A6AA9',
				marginBottom: '16px',
				flexDirection: 'row',
				alignItems: 'flex-start',
				gap: '20px'
			}}>
				<div style={{ flex: 1 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
						<div style={{ fontWeight: '700', fontSize: '17px', color: '#002f5f' }}>{routerBot.name}</div>
						<span style={{ fontSize: '11px', background: '#e8f0fa', color: '#2A6AA9', borderRadius: '4px', padding: '2px 8px', fontWeight: '600' }}>{routerBot.model}</span>
						<span style={{ fontSize: '11px', background: '#f0f4f0', color: '#666', borderRadius: '4px', padding: '2px 8px' }}>Router</span>
					</div>
					<div style={{ color: '#666', fontSize: '13px', marginBottom: '10px' }}>{routerBot.description}</div>
					<div>{renderActionChips(routerBot.actions)}</div>
				</div>
			</div>
		) : null;

		var botCards = otherBots.map(function ([botId, bot]) {
			return (
				<div key={botId} onClick={this.openEditBot(botId, bot)} style={{ ...clickableCardStyle, flex: '1 1 260px', minWidth: '230px', maxWidth: '340px' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
						<div style={{ fontWeight: '700', fontSize: '15px', color: '#002f5f' }}>{bot.name}</div>
						<button onClick={this.openDeleteBot(botId, bot)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontSize: '18px', lineHeight: 1, padding: '2px', flexShrink: 0 }} title="Delete">×</button>
					</div>
					<span style={{ fontSize: '11px', background: '#e8f0fa', color: '#2A6AA9', borderRadius: '4px', padding: '2px 8px', fontWeight: '600', display: 'inline-block', marginBottom: '8px', alignSelf: 'flex-start' }}>{bot.model}</span>
					<div style={{ color: '#666', fontSize: '13px', lineHeight: '1.5', flex: 1, marginBottom: '10px' }}>{bot.description}</div>
					<div style={{ borderTop: '1px solid #eee', paddingTop: '8px' }}>
						{renderActionChips(bot.actions)}
					</div>
				</div>
			);
		}.bind(this));

		var addBotCard = (
			<div
				onClick={this.openAddBot}
				style={{
					...cardStyle,
					flex: '1 1 260px',
					minWidth: '230px',
					maxWidth: '340px',
					border: '2px dashed #c8d8ea',
					boxShadow: 'none',
					alignItems: 'center',
					justifyContent: 'center',
					cursor: 'pointer',
					color: '#2A6AA9',
					minHeight: '140px',
					transition: 'border-color 0.2s, background 0.2s'
				}}
			>
				<div style={{ fontSize: '32px', lineHeight: 1, marginBottom: '8px' }}>+</div>
				<div style={{ fontWeight: '600', fontSize: '14px' }}>Add Bot</div>
			</div>
		);

		// ── Modals ─────────────────────────────────────────────────────────
		var introModal = this.state.editingIntro ? (
			<div className="blackout" tabIndex="1" autoFocus>
				<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '520px', maxWidth: '92vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
					<div className="viewCloser" onClick={this.cancel}></div>
					<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
						<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Virtual Agent</div>
						<div style={{ fontSize: '22px', fontWeight: '700' }}>Messages</div>
					</div>
					<div style={{ padding: '28px 32px 32px' }}>

						<div style={{ width: '100%', marginBottom: '22px' }}>
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
								<label style={modalLabelStyle}>Intro Message</label>
								{this.renderPreviewBtn('_intro', () => this.previewText('_intro', this.state.tempIntro))}
							</div>
							<textarea
								value={this.state.tempIntro}
								onChange={(e) => this.setState({ tempIntro: e.target.value })}
								rows={4}
								style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #2A6AA9', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
								placeholder="Thank you for calling. How can I help you today?"
							/>
						</div>

						<div style={{ width: '100%', marginBottom: '6px' }}>
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
								<label style={modalLabelStyle}>Goodbye Message</label>
								{this.renderPreviewBtn('_goodbye', () => this.previewText('_goodbye', this.state.tempGoodbye))}
							</div>
							<textarea
								value={this.state.tempGoodbye}
								onChange={(e) => this.setState({ tempGoodbye: e.target.value })}
								rows={4}
								style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #2A6AA9', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
								placeholder="Thank you for calling. Have a great day!"
							/>
						</div>

						<div className="error">{this.state.error}</div>
						{this.state.loading ? <div className="loader"></div> : <SaveButton onClick={this.saveIntro} />}
					</div>
				</div>
			</div>
		) : '';

		var aiModal = this.state.editingAI ? (() => {
			var tempAI = this.state.tempAI.ai || {};
			var selectedProvider = tempAI.ttsProvider || 'Google';
			var selectedVoice = tempAI.ttsVoice || '';
			var voices = VOICES_BY_PROVIDER[selectedProvider] || [];
			var providerInfo = VOICE_PROVIDERS.find(function (p) { return p.id === selectedProvider; }) || {};

			return (
				<div className="blackout" tabIndex="1" autoFocus>
					<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '600px', maxWidth: '95vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
						<div className="viewCloser" onClick={this.cancel}></div>
						<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
							<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Virtual Agent</div>
							<div style={{ fontSize: '22px', fontWeight: '700' }}>AI & Voice</div>
						</div>
						<div style={{ padding: '28px 32px 32px', overflowY: 'auto', maxHeight: 'calc(60vh - 80px)' }}>

							{/* TTS Provider tiles */}
							<div style={{ width: '100%', marginBottom: '22px' }}>
								<label style={modalLabelStyle}>Voice Provider</label>
								<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
									{VOICE_PROVIDERS.map(function (p) {
										var isSelected = selectedProvider === p.id;
										return (
											<div key={p.id} onClick={() => this.selectProvider(p.id)} style={{
												border: '2px solid ' + (isSelected ? '#2A6AA9' : '#e0e6ef'),
												borderRadius: '8px',
												padding: '12px 8px',
												cursor: 'pointer',
												background: isSelected ? '#f0f6ff' : '#fff',
												textAlign: 'center',
												transition: 'border-color 0.15s, background 0.15s'
											}}>
												<div style={{ fontWeight: '700', fontSize: '13px', color: isSelected ? '#2A6AA9' : '#333' }}>{p.label}</div>
												<div style={{ fontSize: '11px', color: '#888', marginTop: '3px', lineHeight: '1.3' }}>{p.description}</div>
											</div>
										);
									}.bind(this))}
								</div>
							</div>

							{/* Voice list */}
							<div style={{ width: '100%', marginBottom: '6px' }}>
								<div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '6px' }}>
									<label style={{ ...modalLabelStyle, marginBottom: 0 }}>Voice</label>
									{providerInfo.canPreview && (
										<span style={{ fontSize: '11px', color: '#aaa' }}>Click ▶ to hear a sample</span>
									)}
								</div>
								<div style={{ border: '1px solid #e0e6ef', borderRadius: '8px', overflow: 'hidden' }}>
									{voices.map(function (v, i) {
										var isSelected = selectedVoice === v.id;
										var isPlaying = this.state.playingVoice === v.id;
										var canPreview = providerInfo.canPreview;
										return (
											<div key={v.id} onClick={() => this.selectVoice(v.id)} style={{
												display: 'flex',
												alignItems: 'center',
												padding: '11px 14px',
												background: isSelected ? '#f0f6ff' : '#fff',
												borderBottom: i < voices.length - 1 ? '1px solid #f0f0f0' : 'none',
												cursor: 'pointer',
												transition: 'background 0.1s'
											}}>
												{/* Radio dot */}
												<div style={{
													width: '16px', height: '16px', borderRadius: '50%',
													border: '2px solid ' + (isSelected ? '#2A6AA9' : '#ccc'),
													background: isSelected ? '#2A6AA9' : '#fff',
													display: 'flex', alignItems: 'center', justifyContent: 'center',
													flexShrink: 0, marginRight: '12px'
												}}>
													{isSelected && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }}></div>}
												</div>
												{/* Voice info */}
												<div style={{ flex: 1 }}>
													<div style={{ fontWeight: '600', fontSize: '14px', color: isSelected ? '#2A6AA9' : '#222' }}>{v.label}</div>
													<div style={{ fontSize: '12px', color: '#999', marginTop: '1px' }}>{v.desc}</div>
												</div>
												{/* Preview button */}
												{canPreview && this.renderPreviewBtn(v.id, (e) => { e.stopPropagation(); this.previewVoice(v.id); })}
											</div>
										);
									}.bind(this))}
								</div>
								{!providerInfo.canPreview && (
									<div style={{ fontSize: '12px', color: '#aaa', marginTop: '6px', fontStyle: 'italic' }}>
										Voice preview is not available for this provider.
									</div>
								)}
							</div>

							<div className="error">{this.state.error}</div>
							{this.state.loading
								? <div className="loader"></div>
								: <SaveButton onClick={this.saveAI} />
							}
						</div>
					</div>
				</div>
			);
		})() : '';

		var moodModal = this.state.editingMood ? (() => {
			var moods = (mood.moods) || {};
			return (
				<div className="blackout" tabIndex="1" autoFocus>
					<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '520px', maxWidth: '92vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
						<div className="viewCloser" onClick={this.cancel}></div>
						<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
							<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Virtual Agent</div>
							<div style={{ fontSize: '22px', fontWeight: '700' }}>Tone & Mood</div>
						</div>
						<div style={{ padding: '28px 32px 32px', overflowY: 'auto', maxHeight: 'calc(85vh - 90px)' }}>
							<p style={{ color: '#666', fontSize: '13px', marginBottom: '20px', textAlign: 'center' }}>Controls the personality and response length across all bots.</p>
							<div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
								{MOODS.map(function (m) {
									var def = moods[m] || {};
									var selected = this.state.tempMood === m;
									return (
										<div key={m} onClick={() => this.setState({ tempMood: m })} style={{
											border: '2px solid ' + (selected ? MOOD_COLORS[m] : '#e0e6ef'),
											borderRadius: '8px',
											padding: '12px 16px',
											cursor: 'pointer',
											background: selected ? '#f8fffe' : '#fff',
											transition: 'border-color 0.15s'
										}}>
											<div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
												<span style={{ width: '10px', height: '10px', borderRadius: '50%', background: MOOD_COLORS[m], display: 'inline-block', flexShrink: 0 }}></span>
												<strong style={{ textTransform: 'capitalize', color: '#222' }}>{m}</strong>
												{def.lengthGuidance && <span style={{ fontSize: '12px', color: '#888', marginLeft: 'auto' }}>{def.lengthGuidance}</span>}
											</div>
											<div style={{ fontSize: '13px', color: '#666', paddingLeft: '20px' }}>{def.description}</div>
										</div>
									);
								}.bind(this))}
							</div>
							<div className="error">{this.state.error}</div>
							{this.state.loading ? <div className="loader"></div> : <SaveButton onClick={this.saveMood} />}
						</div>
					</div>
				</div>
			);
		})() : '';

		var failureModal = this.state.editingFailure ? (() => {
			var tf = this.state.tempFailure || {};
			var tfTriggers = tf.triggers || {};
			var queueOpts = Object.keys(queuesData).map(function (id) { return { id: id, name: queuesData[id].name || id }; });
			var flowOpts = Object.keys(flowsData).map(function (id) { return { id: id, name: flowsData[id].name || id }; });
			return (
				<div className="blackout" tabIndex="1" autoFocus>
					<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '560px', maxWidth: '94vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
						<div className="viewCloser" onClick={this.cancel}></div>
						<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
							<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Virtual Agent</div>
							<div style={{ fontSize: '22px', fontWeight: '700' }}>Failure Handling</div>
						</div>
						<div style={{ padding: '28px 32px 32px', overflowY: 'auto', maxHeight: 'calc(80vh - 90px)' }}>

							{/* Which reasons count as a failed attempt — vertical checkbox list */}
							<label style={modalLabelStyle}>Failure reasons</label>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
								{FAILURE_TRIGGERS.map(function (trg) {
									var active = tfTriggers[trg.id] !== false;
									var col = '#c0392b';
									return (
										<div key={trg.id} onClick={this.toggleFailureTrigger(trg.id)} style={{
											padding: '10px 12px', borderRadius: '6px', cursor: 'pointer',
											border: '2px solid ' + (active ? col : '#e0e6ef'),
											background: active ? col + '11' : '#fff',
											transition: 'border-color 0.15s, background 0.15s'
										}}>
											<div style={{ fontWeight: '600', fontSize: '13px', color: active ? col : '#222' }}>{trg.label}</div>
											<div style={{ fontSize: '12px', color: '#888', marginTop: '2px', lineHeight: '1.4' }}>{trg.desc}</div>
										</div>
									);
								}.bind(this))}
							</div>

							{/* Threshold */}
							<label style={modalLabelStyle}>Maximum number of failed attempts</label>
							<input type="number" min="1" value={tf.maxFailedAttempts || 0} onChange={this.setFailureField('maxFailedAttempts')} style={{ ...modalSelectStyle, marginBottom: '20px' }} />

							{/* Action */}
							<label style={modalLabelStyle}>Action after failure path triggered</label>
							<select value={tf.action || 'hangup'} onChange={this.setFailureField('action')} style={{ ...modalSelectStyle, marginBottom: '20px' }}>
								{FAILURE_ACTIONS.map(function (a) { return <option key={a.id} value={a.id}>{a.label}</option>; })}
							</select>

							{/* Conditional: transfer queue */}
							{tf.action === 'transfer' && (
								<div style={{ marginBottom: '20px' }}>
									<label style={modalLabelStyle}>Queue</label>
									<select value={tf.fallbackQueueId || ''} onChange={this.setFailureField('fallbackQueueId')} style={modalSelectStyle}>
										<option value="">— Select a queue —</option>
										{queueOpts.map(function (q) { return <option key={q.id} value={q.id}>{q.name}</option>; })}
									</select>
									<div style={{ marginTop: '16px' }}>
											<label style={modalLabelStyle}>If queue is closed</label>
											<select value={tf.closedAction || 'disconnect'} onChange={this.setFailureField('closedAction')} style={modalSelectStyle}>
												{QUEUE_CLOSED_ACTIONS.map(function (a) { return <option key={a.id} value={a.id}>{a.label}</option>; })}
											</select>
											{tf.closedAction === 'flow' ? (
												<div style={{ marginTop: '16px' }}>
													<label style={modalLabelStyle}>Closed-hours IVR Flow</label>
													<select value={tf.closedFlowId || ''} onChange={this.setFailureField('closedFlowId')} style={modalSelectStyle}>
														<option value="">— Select a flow —</option>
														{flowOpts.map(function (f) { return <option key={f.id} value={f.id}>{f.name}</option>; })}
													</select>
												</div>
											) : (
												<div style={{ marginTop: '16px' }}>
													<label style={modalLabelStyle}>Disconnect message</label>
													<textarea
														value={tf.message || ''}
														onChange={this.setFailureField('message')}
														rows={3}
														style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
														placeholder="Leave blank to use the chatbot's goodbye message."
													/>
												</div>
											)}
										</div>
								</div>
							)}

							{/* Conditional: flow */}
							{tf.action === 'flow' && (
								<div style={{ marginBottom: '20px' }}>
									<label style={modalLabelStyle}>IVR Flow</label>
									<select value={tf.fallbackFlowId || ''} onChange={this.setFailureField('fallbackFlowId')} style={modalSelectStyle}>
										<option value="">— Select a flow —</option>
										{flowOpts.map(function (f) { return <option key={f.id} value={f.id}>{f.name}</option>; })}
									</select>
									</div>
							)}

							{/* Spoken message — hang-up action only (transfer has its own disconnect message) */}
							{tf.action === 'hangup' && (
								<div style={{ marginBottom: '6px' }}>
									<label style={modalLabelStyle}>Spoken message</label>
									<textarea
										value={tf.message || ''}
										onChange={this.setFailureField('message')}
										rows={3}
										style={{ width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
										placeholder="Leave blank to use the chatbot's goodbye message."
									/>
								</div>
							)}

							<div className="error">{this.state.error}</div>
							{this.state.loading ? <div className="loader"></div> : <SaveButton onClick={this.saveFailure} />}
						</div>
					</div>
				</div>
			);
		})() : '';

		var botModal = this.state.editingBot ? (() => {
			var bot = this.state.editingBot;
			var isNew = bot._isNew;
			return (
				<div className="blackout" tabIndex="1" autoFocus>
					<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '620px', maxWidth: '95vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
						<div className="viewCloser" onClick={this.cancel}></div>
						<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
							<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Bots</div>
							<div style={{ fontSize: '22px', fontWeight: '700' }}>{isNew ? 'Add Bot' : (bot._originalName || bot.name)}</div>
						</div>
						<div style={{ padding: '28px 32px 32px', overflowY: 'auto', maxHeight: 'calc(60vh - 80px)' }}>

							{[
								{ label: 'Bot Name', field: 'name', type: 'text' }
							].map(function ({ label, field, type }) {
								return (
									<div key={field} className="snazzyInputBox" style={{ width: '100%' }}>
										<input className="textInput" type={type} required value={bot[field] || ''} onChange={this.editBotField(field)} />
										<span className="inputBar"></span>
										<label className="textLabel">{label}</label>
									</div>
								);
							}.bind(this))}

							<div style={{ width: '100%', marginBottom: '14px' }}>
								<label style={modalLabelStyle}>Description</label>
								<textarea
									required
									value={bot.description || ''}
									onChange={this.editBotField('description')}
									rows={3}
									style={{ width: '100%', padding: '10px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'sans-serif', lineHeight: '1.5' }}
									placeholder="Short summary of what this bot handles..."
								/>
							</div>

							<div style={{ width: '100%', marginBottom: '14px' }}>
								<label style={modalLabelStyle}>Model</label>
								<select value={bot.model || 'gpt-4o-mini'} onChange={this.editBotField('model')} style={modalSelectStyle}>
									{LLM_MODELS.map(function (m) { return <option key={m.id} value={m.id}>{m.label} — {m.description}</option>; })}
								</select>
							</div>

							<div style={{ width: '100%', display: 'flex', gap: '16px', marginBottom: '14px' }}>
								{[{ label: 'Max Tokens', field: 'maxTokens' }, { label: 'Max History', field: 'maxHistory' }].map(function ({ label, field }) {
									return (
										<div key={field} style={{ flex: 1 }}>
											<label style={modalLabelStyle}>{label}</label>
											<input type="number" value={bot[field] || 0} onChange={this.editBotField(field)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
										</div>
									);
								}.bind(this))}
							</div>

							<div style={{ width: '100%', marginBottom: '14px' }}>
								<label style={modalLabelStyle}>System Prompt</label>
								<textarea
									value={bot.systemPrompt || ''}
									onChange={this.editBotField('systemPrompt')}
									rows={5}
									style={{ width: '100%', padding: '10px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'sans-serif', lineHeight: '1.5' }}
									placeholder="Define this bot's personality and knowledge domain..."
								/>
							</div>

							<div style={{ width: '100%', marginBottom: '14px' }}>
								<label style={modalLabelStyle}>Routing Hints</label>
								<textarea
									value={bot.routingHints || ''}
									onChange={this.editBotField('routingHints')}
									rows={3}
									style={{ width: '100%', padding: '10px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'sans-serif', lineHeight: '1.5' }}
									placeholder="Optional. Example phrases or topics that should route a caller to this bot — e.g. 'refund, disputed charge, billed twice, cancel autopay'. Helps the router pick this bot for things the description doesn't mention."
								/>
								<div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
									Only used to route callers to the right bot — not shown to callers or used in replies.
								</div>
							</div>

							{/* Capabilities */}
							<div style={{ width: '100%', marginBottom: '6px' }}>
								<div style={modalLabelStyle}>Capabilities</div>
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
									{BOT_CAPABILITIES.map(function (cap) {
										var active = (bot.actions || []).indexOf(cap.id) > -1;
										var col = ACTION_COLORS[cap.id] || '#2A6AA9';
										return (
											<div key={cap.id} onClick={this.toggleBotAction(cap.id)} style={{
												padding: '6px 16px', borderRadius: '4px', cursor: 'pointer',
												fontSize: '13px', fontWeight: '600',
												border: '2px solid ' + col,
												background: active ? col : '#fff',
												color: active ? '#fff' : col,
												transition: 'background 0.15s, color 0.15s'
											}}>
												{cap.label}
											</div>
										);
									}.bind(this))}
								</div>

								{/* Knowledge Base checkboxes — shown when Knowledge Base capability is active */}
								{(bot.actions || []).indexOf('knowledgeBase') > -1 && (function () {
									var kEntries = Object.entries(knowledge);
									var col = ACTION_COLORS.knowledgeBase;
									return (
										<div style={{ marginBottom: '14px', padding: '12px 14px', background: col + '11', borderRadius: '7px', border: '1px solid ' + col + '44' }}>
											<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: col, marginBottom: '8px' }}>Knowledge Base</div>
											{kEntries.length === 0 ? (
												<div style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>No knowledge entries defined yet. Add entries in the Knowledge Base section.</div>
											) : (
												<div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
													{kEntries.map(function ([kId, entry]) {
														var active = (bot.knowledgeIds || []).indexOf(kId) > -1;
														return (
															<div key={kId} onClick={this.toggleBotKnowledge(kId)} style={{
																display: 'flex', alignItems: 'center', gap: '10px',
																padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
																border: '2px solid ' + (active ? col : '#e0e6ef'),
																background: active ? col + '11' : '#fff',
																transition: 'border-color 0.15s, background 0.15s'
															}}>
																<div style={{
																	width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
																	border: '2px solid ' + (active ? col : '#ccc'),
																	background: active ? col : '#fff',
																	display: 'flex', alignItems: 'center', justifyContent: 'center'
																}}>
																	{active && <span style={{ color: '#fff', fontSize: '11px', lineHeight: 1, fontWeight: '700' }}>✓</span>}
																</div>
																<div style={{ flex: 1, minWidth: 0 }}>
																	<div style={{ fontWeight: '600', fontSize: '13px', color: active ? col : '#222' }}>{entry.name}</div>
																	<div style={{ fontSize: '12px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.content}</div>
																</div>
															</div>
														);
													}.bind(this))}
												</div>
											)}
										</div>
									);
								}.bind(this))()}

								{/* Transfer queues — shown when Transfer capability is active */}
								{(bot.actions || []).indexOf('transferAgent') > -1 && (function () {
									var queuesRaw = ((this.props.data.Queues || {}).data) || {};
									var queuesList = Object.keys(queuesRaw).map(function (id) { return queuesRaw[id]; });
									var col = ACTION_COLORS.transferAgent;
									return (
										<div style={{ marginBottom: '14px', padding: '12px 14px', background: '#fff8f3', borderRadius: '7px', border: '1px solid #f5d5bb' }}>
											<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: col, marginBottom: '8px' }}>Transfer Queues</div>
											{queuesList.length === 0 ? (
												<div style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>No queues defined yet. Add them in the Queues section.</div>
											) : (
												<div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
													{queuesList.map(function (q) {
														var active = (bot.transferQueueIds || []).indexOf(q.id) > -1;
														return (
															<div key={q.id} onClick={function () {
																var tempBot = JSON.parse(JSON.stringify(this.state.editingBot));
																if (!tempBot.transferQueueIds) tempBot.transferQueueIds = [];
																var idx = tempBot.transferQueueIds.indexOf(q.id);
																if (idx > -1) { tempBot.transferQueueIds.splice(idx, 1); } else { tempBot.transferQueueIds.push(q.id); }
																this.setState({ editingBot: tempBot });
															}.bind(this)} style={{
																display: 'flex', alignItems: 'center', gap: '10px',
																padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
																border: '2px solid ' + (active ? col : '#e0e6ef'),
																background: active ? col + '11' : '#fff',
																transition: 'border-color 0.15s, background 0.15s'
															}}>
																<div style={{
																	width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
																	border: '2px solid ' + (active ? col : '#ccc'),
																	background: active ? col : '#fff',
																	display: 'flex', alignItems: 'center', justifyContent: 'center'
																}}>
																	{active && <span style={{ color: '#fff', fontSize: '11px', lineHeight: 1, fontWeight: '700' }}>✓</span>}
																</div>
																<div style={{ flex: 1, minWidth: 0 }}>
																	<div style={{ fontWeight: '600', fontSize: '13px', color: active ? col : '#222' }}>{q.name}</div>
																</div>
															</div>
														);
													}.bind(this))}
												</div>
											)}
										</div>
									);
								}.bind(this))()}

								{/* Integrations checkboxes — shown when Integrations capability is active */}
								{(bot.actions || []).indexOf('integrations') > -1 && (function () {
									var actionsData = ((this.props.data.Actions || {}).data) || {};
									var actionEntries = Object.entries(actionsData);
									var TYPE_COLORS_LOCAL = { restApi: '#1a5276', custom: '#6c3483' };
									var TYPE_LABELS_LOCAL = { restApi: 'REST API', custom: 'Custom' };
									var col = ACTION_COLORS.integrations;
									return (
										<div style={{ marginBottom: '14px', padding: '12px 14px', background: col + '11', borderRadius: '7px', border: '1px solid ' + col + '44' }}>
											<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: col, marginBottom: '8px' }}>Integrations</div>
											{actionEntries.length === 0 ? (
												<div style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>No integrations defined yet. Add them in the Actions section.</div>
											) : (
												<div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
													{actionEntries.map(function ([aId, action]) {
														var active = (bot.actionIds || []).indexOf(aId) > -1;
														var col = TYPE_COLORS_LOCAL[action.type] || '#555';
														return (
															<div key={aId} onClick={this.toggleBotActionId(aId)} style={{
																display: 'flex', alignItems: 'center', gap: '10px',
																padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
																border: '2px solid ' + (active ? col : '#e0e6ef'),
																background: active ? col + '11' : '#fff',
																transition: 'border-color 0.15s, background 0.15s'
															}}>
																<div style={{
																	width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
																	border: '2px solid ' + (active ? col : '#ccc'),
																	background: active ? col : '#fff',
																	display: 'flex', alignItems: 'center', justifyContent: 'center'
																}}>
																	{active && <span style={{ color: '#fff', fontSize: '11px', lineHeight: 1, fontWeight: '700' }}>✓</span>}
																</div>
																<div style={{ flex: 1, minWidth: 0 }}>
																	<div style={{ fontWeight: '600', fontSize: '13px', color: active ? col : '#222', display: 'flex', alignItems: 'center', gap: '6px' }}>
																		{action.name}
																		<span style={{ background: col, color: '#fff', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: '700' }}>
																			{TYPE_LABELS_LOCAL[action.type] || action.type}
																		</span>
																	</div>
																	{action.description && <div style={{ fontSize: '12px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{action.description}</div>}
																</div>
															</div>
														);
													}.bind(this))}
												</div>
											)}
										</div>
									);
								}.bind(this))()}
							</div>

							<div className="error">{this.state.error}</div>
							{this.state.loading ? <div className="loader"></div> : <SaveButton onClick={this.saveBot}>{isNew ? 'Add Bot' : 'Save'}</SaveButton>}
						</div>
					</div>
				</div>
			);
		})() : '';

		var deleteModal = this.state.deletingBot ? (
			<div className="blackout" tabIndex="1" autoFocus>
				<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '400px', maxWidth: '92vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
					<div className="viewCloser" onClick={this.cancel}></div>
					<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
						<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Bots</div>
						<div style={{ fontSize: '22px', fontWeight: '700' }}>Remove Bot</div>
					</div>
					<div style={{ padding: '28px 32px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<p style={{ color: '#555', textAlign: 'center', marginBottom: '25px' }}>
							Are you sure you want to remove <strong>{this.state.deletingBot.bot.name}</strong>? This cannot be undone.
						</p>
						{this.state.loading
							? <div className="loader"></div>
							: <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
								<DeleteButton onClick={this.confirmDeleteBot} />
							</div>
						}
					</div>
				</div>
			</div>
		) : '';

		// ── Chatbot selector modals ────────────────────────────────────────
		var renameChatbotModal = this.state.renamingChatbot ? (
			<div className="blackout" tabIndex="1" autoFocus>
				<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '400px', maxWidth: '92vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
					<div className="viewCloser" onClick={this.cancel}></div>
					<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
						<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Virtual Agents</div>
						<div style={{ fontSize: '22px', fontWeight: '700' }}>Rename Virtual Agent</div>
					</div>
					<div style={{ padding: '28px 32px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<div className="snazzyInputBox" style={{ width: '100%' }}>
							<input className="textInput" type="text" required value={this.state.newChatbotName} onChange={(e) => this.setState({ newChatbotName: e.target.value, error: '' })} onKeyUp={(e) => { if (e.keyCode === 13) this.confirmRenameChatbot(); }} autoFocus />
							<span className="inputBar"></span>
							<label className="textLabel">New Name</label>
						</div>
						<div className="error">{this.state.error}</div>
						<SaveButton onClick={this.confirmRenameChatbot}>Rename</SaveButton>
					</div>
				</div>
			</div>
		) : '';

		var addChatbotModal = this.state.addingChatbot ? (
			<div className="blackout" tabIndex="1" autoFocus>
				<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '400px', maxWidth: '92vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
					<div className="viewCloser" onClick={this.cancel}></div>
					<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
						<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Virtual Agents</div>
						<div style={{ fontSize: '22px', fontWeight: '700' }}>New Virtual Agent</div>
					</div>
					<div style={{ padding: '28px 32px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<div className="snazzyInputBox" style={{ width: '100%' }}>
							<input className="textInput" type="text" required value={this.state.newChatbotName} onChange={(e) => this.setState({ newChatbotName: e.target.value, error: '' })} onKeyUp={(e) => { if (e.keyCode === 13) this.confirmAddChatbot(); }} />
							<span className="inputBar"></span>
							<label className="textLabel">Virtual Agent Name</label>
						</div>
						<div className="error">{this.state.error}</div>
						<SaveButton onClick={this.confirmAddChatbot}>Create</SaveButton>
					</div>
				</div>
			</div>
		) : '';

		var deleteChatbotModal = this.state.deletingChatbot ? (
			<div className="blackout" tabIndex="1" autoFocus>
				<div className="pop" style={{ display: 'flex', flexDirection: 'column', width: '400px', maxWidth: '92vw', padding: '0', position: 'relative', overflow: 'hidden' }}>
					<div className="viewCloser" onClick={this.cancel}></div>
					<div style={{ background: '#002f5f', padding: '24px 32px', color: '#fff' }}>
						<div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Virtual Agents</div>
						<div style={{ fontSize: '22px', fontWeight: '700' }}>Remove Virtual Agent</div>
					</div>
					<div style={{ padding: '28px 32px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<p style={{ color: '#555', textAlign: 'center', marginBottom: '25px' }}>
							Are you sure you want to remove <strong>{data.name || activeKey}</strong>? This cannot be undone.
						</p>
						<div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
							<DeleteButton onClick={this.confirmDeleteChatbot} />
						</div>
					</div>
				</div>
			</div>
		) : '';

		// ── Final Render ───────────────────────────────────────────────────
		return (
			<div style={{ padding: '20px', overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>

				{this.state.generatingIVR && (
					<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
						<div className="loader" style={{ width: '48px', height: '48px', borderWidth: '5px' }}></div>
						<div style={{ color: '#fff', fontSize: '16px', fontWeight: '600', letterSpacing: '0.5px' }}>Generating IVR flow…</div>
						{this.props.data._ivrGenProgress && (
							<div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>{this.props.data._ivrGenProgress}</div>
						)}
					</div>
				)}

				{/* Chatbot Selector */}
				<div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
					<select
						value={activeKey}
						onChange={(e) => this.setState({ activeChatbot: e.target.value })}
						style={{ display: 'inline-block', width: '250px', height: '40px', padding: '0 10px', border: '1px solid #ddd', borderRadius: '4px', fontWeight: 'bold', fontSize: '20px', color: '#137', lineHeight: '24px' }}
					>
						{chatbotKeys.map(function (k) {
							return <option key={k} value={k}>{chatbots[k].name || k}</option>;
						})}
					</select>
					<button onClick={this.openAddChatbot} style={{ height: '40px', width: '40px', padding: 0, borderRadius: '4px', border: '1px solid #ddd', background: '#fff', fontSize: '22px', color: '#2A6AA9', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }} title="Add new virtual agent">+</button>
					{chatbotKeys.length > 0 && (
						<div style={{ position: 'relative' }}>
							<button
								onClick={() => this.setState(function (s) { return { chatbotMenuOpen: !s.chatbotMenuOpen }; })}
								onBlur={() => setTimeout(() => this.setState({ chatbotMenuOpen: false }), 150)}
								style={{ height: '40px', width: '40px', padding: 0, borderRadius: '4px', border: '1px solid #ddd', background: this.state.chatbotMenuOpen ? '#f0f4fa' : '#fff', fontSize: '22px', color: '#555', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}
								title="Virtual agent options"
							>⋮</button>
							{this.state.chatbotMenuOpen && (
								<div style={{ position: 'absolute', top: '44px', left: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.13)', zIndex: 100, minWidth: '140px', overflow: 'hidden' }}>
									<button onClick={this.openRenameChatbot} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid #eee', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#222' }}>Rename</button>
									<button onClick={this.duplicateChatbot} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid #eee', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#222' }}>Duplicate</button>
									{chatbotKeys.length > 1 && (
										<button onClick={this.openDeleteChatbot} style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#c0392b' }}>Delete</button>
									)}
								</div>
							)}
						</div>
					)}

					{chatbotKeys.length > 0 && (
						<button
							onClick={this.generateIVR}
							disabled={this.state.generatingIVR}
							style={{ height: '40px', padding: '0 16px', borderRadius: '4px', border: '1px solid #002f5f', background: '#002f5f', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: this.state.generatingIVR ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: this.state.generatingIVR ? 0.6 : 1 }}
							title="Generate an IVR flow from this virtual agent using AI"
						>Generate IVR</button>
					)}
				</div>

				{/* Settings Cards */}
				<div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
					{introCard}
					{aiCard}
					{moodCard}
					{failureCard}
				</div>

				{/* Bots Section */}
				<div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
					<div style={{ fontWeight: '700', fontSize: '13px', letterSpacing: '1px', textTransform: 'uppercase', color: '#888', flex: 1 }}>Bots</div>
				</div>

				{routerCard}

				<div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
					{botCards}
					{addBotCard}
				</div>

				{/* Modals */}
				{introModal}
				{aiModal}
				{moodModal}
				{failureModal}
				{botModal}
				{deleteModal}
				{addChatbotModal}
				{renameChatbotModal}
				{deleteChatbotModal}

				{/* Publish Button */}
				{this.state.isDirty && (
					<div style={{
						position: 'fixed', bottom: '30px', right: '30px', zIndex: 50,
						display: 'flex', alignItems: 'center', gap: '14px',
						background: '#fff', border: '1px solid #dde3ea',
						borderRadius: '12px', padding: '12px 20px',
						boxShadow: '0 8px 32px rgba(0,0,0,0.16)'
					}}>
						<span style={{ fontSize: '13px', color: '#888', fontWeight: '500' }}>Unpublished changes</span>
						{this.state.loading
							? <div className="loader" style={{ margin: 0 }}></div>
							: <button className="button" style={{ border: 'none', cursor: 'pointer', margin: 0 }} onClick={this.publish}>Publish</button>
						}
					</div>
				)}
			</div>
		);
	}
}

const icon = "menuBot";
const roles = ["View", "Add", "Edit", "Delete"];
export { AIChatbots, icon, roles };
